import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, LifeBuoy, Loader2, Plus, Send } from 'lucide-react';
import { supportTickets } from '../services/api';

/*
 * Pedidos de suporte — LADO DO GINÁSIO.
 *
 * Até aqui o suporte era telefone e WhatsApp: o pedido existia na cabeça de quem atendeu e não
 * ficava registo nenhum. Aqui o administrador do ginásio escreve o que se passa (assunto,
 * descrição e prioridade), vê em que estado está e continua a conversa com quem responde.
 *
 * Regras que este ecrã não pode perder:
 *  - falha de rede ≠ lista vazia: uma falha aparece como falha e deixa repetir;
 *  - um pedido fechado DIZ que está fechado e não mostra a caixa de resposta (o servidor também
 *    recusa — o ecrã não promete o que a API nega);
 *  - alvos ≥44 px e texto ≥12 px: isto é usado no telemóvel do dono, a 390 px.
 */

const TAP = 44;

export const STATUS_LABELS = {
  Aberto: 'Aberto',
  EmCurso: 'Em curso',
  Resolvido: 'Resolvido',
  Fechado: 'Fechado',
};

export const PRIORITY_OPTIONS = [
  { value: 'Baixa', label: 'Baixa' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Alta', label: 'Alta' },
  { value: 'Urgente', label: 'Urgente' },
];

const fmtDate = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const statusStyle = (status) => {
  switch (status) {
    case 'EmCurso':
      return { background: 'var(--warning-bg, #fff4e5)', color: 'var(--warning-text, #8a5a00)', label: 'Em curso' };
    case 'Resolvido':
      return { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)', label: 'Resolvido' };
    case 'Fechado':
      return { background: 'var(--surface-sunken, #f1f3f5)', color: 'var(--text-muted, #5a6270)', label: 'Fechado' };
    default:
      return { background: 'var(--accent-soft, #e8f0ff)', color: 'var(--accent-primary-hover, #1a4fa0)', label: 'Aberto' };
  }
};

const priorityStyle = (priority) => {
  switch (priority) {
    case 'Urgente':
      return { background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)' };
    case 'Alta':
      return { background: 'var(--warning-bg, #fff4e5)', color: 'var(--warning-text, #8a5a00)' };
    default:
      return { background: 'var(--surface-sunken, #f1f3f5)', color: 'var(--text-muted, #5a6270)' };
  }
};

const chip = (style, text) => (
  <span style={{
    display: 'inline-block', padding: '0.25rem 0.6rem', borderRadius: '999px',
    fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4,
    background: style.background, color: style.color,
  }}>
    {text}
  </span>
);

const SupportTicketsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('list');          // list | form | thread
  const [draft, setDraft] = useState({ subject: '', description: '', priority: 'Normal' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [thread, setThread] = useState(null);        // { ticket, messages }
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await supportTickets.list();
      setData(res.data);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler os seus pedidos de suporte.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openThread = async (ticketId) => {
    setView('thread');
    setThread(null);
    setThreadError(null);
    setReply('');
    setThreadLoading(true);
    try {
      const res = await supportTickets.get(ticketId);
      setThread(res.data);
    } catch (e) {
      setThreadError(apiMessage(e, 'Não foi possível abrir este pedido.'));
    } finally {
      setThreadLoading(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setFormError(null);
    try {
      const res = await supportTickets.create({
        subject: draft.subject.trim(),
        description: draft.description.trim(),
        priority: draft.priority,
      });
      toast.success(res.data?.message || 'Pedido de suporte aberto.');
      setDraft({ subject: '', description: '', priority: 'Normal' });
      await load();
      setView('list');
    } catch (err) {
      // O motivo do servidor é o que o ginásio precisa de ler (assunto curto, descrição vazia…).
      setFormError(apiMessage(err, 'Não foi possível abrir o pedido.'));
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    const text = reply.trim();
    if (!text) return;
    setSending(true);
    setThreadError(null);
    try {
      const res = await supportTickets.reply(thread.ticket.id, text);
      setThread(res.data);
      setReply('');
      await load();
    } catch (err) {
      setThreadError(apiMessage(err, 'Não foi possível enviar a mensagem.'));
    } finally {
      setSending(false);
    }
  };

  const counters = data?.counters || {};
  const tickets = data?.tickets || [];

  // ── Lista ─────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div style={{ padding: '1rem', maxWidth: '56rem', margin: '0 auto' }} data-testid="support-page">
        <header style={{ marginBottom: '1rem' }}>
          <h1 style={{ fontSize: '1.25rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <LifeBuoy size={20} /> Suporte GyMag
          </h1>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted, #5a6270)', margin: '0.35rem 0 0' }}>
            Escreva o que se passa. O pedido fica registado aqui e a equipa responde neste ecrã — já não depende de
            uma chamada.
          </p>
        </header>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="support-new"
            onClick={() => { setFormError(null); setView('form'); }}
            style={{ minHeight: TAP, minWidth: TAP, display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
          >
            <Plus size={16} /> Abrir pedido
          </button>
          {Object.keys(STATUS_LABELS).map((status) => (
            <span key={status} style={{
              minHeight: TAP, display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              padding: '0 0.7rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
              background: statusStyle(status).background, color: statusStyle(status).color,
            }}>
              {STATUS_LABELS[status]} · {counters[{ Aberto: 'aberto', EmCurso: 'emCurso', Resolvido: 'resolvido', Fechado: 'fechado' }[status]] || 0}
            </span>
          ))}
        </div>

        {loading && (
          <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }} data-testid="support-loading">
            <Loader2 size={16} className="spin" /> A ler os seus pedidos…
          </p>
        )}

        {!loading && loadError && (
          <div role="alert" data-testid="support-error" style={{
            border: '1px solid var(--error-border, #f3c2c2)', background: 'var(--error-bg, #fdeaea)',
            color: 'var(--error-text, #a11616)', borderRadius: '0.6rem', padding: '0.9rem', fontSize: '0.875rem',
          }}>
            <p style={{ margin: '0 0 0.6rem' }}>{loadError}</p>
            <button type="button" className="btn btn-secondary" onClick={load} data-testid="support-retry"
              style={{ minHeight: TAP, fontSize: '0.875rem' }}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !loadError && tickets.length === 0 && (
          <p data-testid="support-empty" style={{ fontSize: '0.875rem', color: 'var(--text-muted, #5a6270)' }}>
            Ainda não abriu nenhum pedido. Quando precisar, é aqui.
          </p>
        )}

        {!loading && !loadError && tickets.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.6rem' }} data-testid="support-list">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <button
                  type="button"
                  data-testid={`support-ticket-${ticket.id}`}
                  onClick={() => openThread(ticket.id)}
                  style={{
                    width: '100%', textAlign: 'left', minHeight: TAP, cursor: 'pointer',
                    border: '1px solid var(--border-subtle, #dde1e6)', borderRadius: '0.7rem',
                    background: 'var(--surface, #fff)', padding: '0.8rem 0.9rem',
                    display: 'block', font: 'inherit', color: 'inherit',
                  }}
                >
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.9375rem' }}>{ticket.subject}</strong>
                    {chip(statusStyle(ticket.status), ticket.statusLabel || ticket.status)}
                    {chip(priorityStyle(ticket.priority), ticket.priorityLabel || ticket.priority)}
                  </span>
                  <span style={{ display: 'block', marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--text-muted, #5a6270)' }}>
                    Aberto em {fmtDate(ticket.createdAt)} por {ticket.createdBy} · {ticket.messageCount === 1 ? '1 mensagem' : `${ticket.messageCount} mensagens`}
                    {ticket.closedBy ? ` · fechado por ${ticket.closedBy}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ── Formulário ────────────────────────────────────────────────────────
  if (view === 'form') {
    return (
      <div style={{ padding: '1rem', maxWidth: '40rem', margin: '0 auto' }} data-testid="support-form">
        <button type="button" onClick={() => setView('list')} data-testid="support-back"
          style={{ minHeight: TAP, background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.875rem', color: 'var(--accent-primary, #2563eb)', marginBottom: '0.75rem' }}>
          <ArrowLeft size={16} /> Voltar
        </button>

        <h2 style={{ fontSize: '1.125rem', margin: '0 0 0.35rem' }}>Abrir um pedido de suporte</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted, #5a6270)', margin: '0 0 1rem' }}>
          Quanto mais concreto, mais depressa se resolve: o que estava a fazer, o que apareceu e quando.
        </p>

        <form onSubmit={submit}>
          <label htmlFor="support-subject" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.3rem' }}>
            Assunto
          </label>
          <input
            id="support-subject"
            data-testid="support-subject"
            value={draft.subject}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
            maxLength={200}
            required
            style={{ width: '100%', minHeight: TAP, padding: '0.5rem 0.65rem', fontSize: '0.9375rem', borderRadius: '0.5rem', border: '1px solid var(--border-subtle, #dde1e6)', marginBottom: '0.8rem' }}
          />

          <label htmlFor="support-description" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.3rem' }}>
            O que se passa
          </label>
          <textarea
            id="support-description"
            data-testid="support-description"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            rows={5}
            maxLength={4000}
            required
            style={{ width: '100%', minHeight: TAP, padding: '0.5rem 0.65rem', fontSize: '0.9375rem', borderRadius: '0.5rem', border: '1px solid var(--border-subtle, #dde1e6)', marginBottom: '0.8rem' }}
          />

          <label htmlFor="support-priority" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.3rem' }}>
            Prioridade
          </label>
          <select
            id="support-priority"
            data-testid="support-priority"
            value={draft.priority}
            onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))}
            style={{ width: '100%', minHeight: TAP, padding: '0.5rem 0.65rem', fontSize: '0.9375rem', borderRadius: '0.5rem', border: '1px solid var(--border-subtle, #dde1e6)', marginBottom: '1rem' }}
          >
            {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>

          {formError && (
            <p role="alert" data-testid="support-form-error" style={{ fontSize: '0.875rem', color: 'var(--error-text, #a11616)', margin: '0 0 0.8rem' }}>
              {formError}
            </p>
          )}

          <button type="submit" className="btn btn-primary" data-testid="support-submit" disabled={saving}
            style={{ minHeight: TAP, width: '100%', fontSize: '0.9375rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
            {saving ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            {saving ? 'A enviar…' : 'Enviar pedido'}
          </button>
        </form>
      </div>
    );
  }

  // ── Conversa ──────────────────────────────────────────────────────────
  const selected = thread?.ticket || null;
  const messages = thread?.messages || [];
  const closed = selected ? (selected.status === 'Fechado' || selected.acceptsMessages === false) : false;

  return (
    <div style={{ padding: '1rem', maxWidth: '40rem', margin: '0 auto' }} data-testid="support-thread">
      <button type="button" onClick={() => { setView('list'); setThread(null); }} data-testid="support-back"
        style={{ minHeight: TAP, background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.875rem', color: 'var(--accent-primary, #2563eb)', marginBottom: '0.75rem' }}>
        <ArrowLeft size={16} /> Os meus pedidos
      </button>

      {threadLoading && (
        <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <Loader2 size={16} className="spin" /> A abrir o pedido…
        </p>
      )}

      {!threadLoading && threadError && !thread && (
        <div role="alert" style={{
          border: '1px solid var(--error-border, #f3c2c2)', background: 'var(--error-bg, #fdeaea)',
          color: 'var(--error-text, #a11616)', borderRadius: '0.6rem', padding: '0.9rem', fontSize: '0.875rem',
        }}>
          <p style={{ margin: '0 0 0.6rem' }}>{threadError}</p>
          <button type="button" className="btn btn-secondary" onClick={() => setView('list')} style={{ minHeight: TAP, fontSize: '0.875rem' }}>
            Voltar aos pedidos
          </button>
        </div>
      )}

      {selected && (
        <>
          <h2 style={{ fontSize: '1.125rem', margin: '0 0 0.4rem' }}>{selected.subject}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginBottom: '0.35rem' }}>
            {chip(statusStyle(selected.status), selected.statusLabel || selected.status)}
            {chip(priorityStyle(selected.priority), selected.priorityLabel || selected.priority)}
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted, #5a6270)', margin: '0 0 1rem' }}>
            Aberto em {fmtDate(selected.createdAt)} por {selected.createdBy}.
            {selected.statusChangedBy ? ` Estado alterado por ${selected.statusChangedBy} em ${fmtDate(selected.statusChangedAt)}.` : ''}
          </p>

          <ol style={{ listStyle: 'none', margin: '0 0 1rem', padding: 0, display: 'grid', gap: '0.6rem' }} data-testid="support-messages">
            {messages.map((m) => (
              <li key={m.id} style={{
                border: '1px solid var(--border-subtle, #dde1e6)', borderRadius: '0.7rem', padding: '0.7rem 0.8rem',
                background: m.fromPortal ? 'var(--accent-soft, #e8f0ff)' : 'var(--surface, #fff)',
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {m.authorRoleLabel || (m.fromPortal ? 'Suporte GyMag' : 'Ginásio')}
                  <span style={{ fontWeight: 400, color: 'var(--text-muted, #5a6270)' }}> · {m.author} · {fmtDate(m.createdAt)}</span>
                </div>
                <p style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', margin: 0 }}>{m.body}</p>
              </li>
            ))}
          </ol>

          {closed ? (
            <p data-testid="support-closed-notice" style={{
              fontSize: '0.875rem', borderRadius: '0.6rem', padding: '0.8rem',
              background: 'var(--surface-sunken, #f1f3f5)', color: 'var(--text-muted, #5a6270)',
              border: '1px solid var(--border-subtle, #dde1e6)', margin: 0,
            }}>
              Este pedido está fechado e não aceita mais mensagens.
              {selected.closedBy ? ` Fechado por ${selected.closedBy} em ${fmtDate(selected.closedAt)}.` : ''}
              {' '}Se voltar a acontecer, abra um pedido novo — o histórico deste fica aqui.
            </p>
          ) : (
            <form onSubmit={sendReply}>
              <label htmlFor="support-reply" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                Responder
              </label>
              <textarea
                id="support-reply"
                data-testid="support-reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                maxLength={4000}
                style={{ width: '100%', minHeight: TAP, padding: '0.5rem 0.65rem', fontSize: '0.9375rem', borderRadius: '0.5rem', border: '1px solid var(--border-subtle, #dde1e6)' }}
              />
              {threadError && (
                <p role="alert" data-testid="support-thread-error" style={{ fontSize: '0.875rem', color: 'var(--error-text, #a11616)', margin: '0.4rem 0' }}>
                  {threadError}
                </p>
              )}
              <button type="submit" className="btn btn-primary" data-testid="support-send" disabled={sending || !reply.trim()}
                style={{ minHeight: TAP, marginTop: '0.5rem', width: '100%', fontSize: '0.9375rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                {sending ? 'A enviar…' : 'Enviar mensagem'}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default SupportTicketsPage;
